import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import campusDrone from "../assets/JR INSTITUTE DRONE.mp4";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaCamera,
  FaPlay,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const ICON_MAP = {
  location: FaMapMarkerAlt,
  calendar: FaCalendarAlt,
  users: FaUsers,
  play: FaPlay,
  clock: FaClock,
  camera: FaCamera,
};

const FALLBACK_HERO = {
  title: "Discover Our Campus",
  subtitle:
    "Experience the vibrant community and world-class facilities that make JR Institute the perfect place for your educational journey.",
  stats: [
    { label: "Arusha, Tanzania", icon: "location" },
    { label: "Tours Available Daily", icon: "calendar" },
    { label: "Personalized Experience", icon: "users" },
  ],
};

const FALLBACK_TOURS = [
  {
    id: "virtual",
    title: "Virtual Campus Tour",
    description:
      "Explore our campus from anywhere in the world with our interactive 360° virtual tour.",
    duration: "15-20 minutes",
    icon: "play",
    features: [
      "360° Interactive Views",
      "Audio Commentary",
      "Available 24/7",
      "Mobile Friendly",
    ],
    buttonText: "Start Virtual Tour",
  },
  {
    id: "in-person",
    title: "In-Person Campus Visit",
    description:
      "Schedule a personalized campus visit with our admissions team and current students.",
    duration: "2-3 hours",
    icon: "users",
    features: [
      "Personalized Tour",
      "Meet Faculty & Students",
      "Q&A Session",
      "Campus Facilities",
    ],
    buttonText: "Schedule Visit",
  },
  {
    id: "group",
    title: "Group Tours",
    description:
      "Perfect for schools, organizations, and large groups interested in our programs.",
    duration: "1-2 hours",
    icon: "users",
    features: [
      "Customized Experience",
      "Program Presentations",
      "Campus Facilities",
      "Lunch Options",
    ],
    buttonText: "Book Group Tour",
  },
];

/** No stock photos — highlights and gallery come from CMS (`highlights` / `gallery` components). */
const FALLBACK_HIGHLIGHTS = [];

const FALLBACK_PHOTOS = [];

const FALLBACK_VISIT = {
  schedule: { weekday: "9:00 AM - 4:00 PM", saturday: "10:00 AM - 2:00 PM", sunday: "By appointment only" },
  expectations: ["Campus facilities tour", "Meet with admissions staff", "Q&A with current students", "Program-specific information"],
  groupTours: "Special arrangements available for schools, organizations, and groups of 10 or more. Please contact us at least one week in advance.",
};

const FALLBACK_CONTACT = {
  phone: "+255 754 360 590",
  email: "admissions@jriit.ac.tz",
  address: "JR Institute of Information Technology\nAdmissions Office\nArusha\nP.O. Box 7071 Namanga Road, Sakina Area Arusha Tanzania",
  scheduleButtonText: "Schedule Your Visit",
  mapButtonText: "Download Campus Map",
};

function safeParse(str, fallback) {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}

const CampusTour = () => {
  const [selectedTour, setSelectedTour] = useState("virtual");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [hero, setHero] = useState(FALLBACK_HERO);
  const [toursMeta, setToursMeta] = useState({ title: "Choose Your Tour Experience", subtitle: "Whether you prefer to explore virtually or visit us in person, we have the perfect tour option for you." });
  const [tourOptions, setTourOptions] = useState(FALLBACK_TOURS);
  const [hlMeta, setHlMeta] = useState({ title: "Campus Highlights", subtitle: "Explore our state-of-the-art facilities and modern learning environments." });
  const [campusHighlights, setCampusHighlights] = useState(FALLBACK_HIGHLIGHTS);
  const [galMeta, setGalMeta] = useState({ title: "Campus Photo Gallery", subtitle: "Take a visual journey through our beautiful campus and facilities." });
  const [campusPhotos, setCampusPhotos] = useState(FALLBACK_PHOTOS);
  const [visitTitle, setVisitTitle] = useState("Plan Your Visit");
  const [visitData, setVisitData] = useState(FALLBACK_VISIT);
  const [contactTitle, setContactTitle] = useState("Contact Admissions");
  const [contactData, setContactData] = useState(FALLBACK_CONTACT);
  const [visibleSectionKeys, setVisibleSectionKeys] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await cmsApi.getPageComponents("campus-tour");
        if (!result?.success) return;
        const list = result.components || [];
        if (list.length === 0) return;

        const map = {};
        const keys = [];
        for (const c of list) {
          map[c.componentName] = c;
          keys.push(c.componentName);
        }
        setVisibleSectionKeys(keys);

        if (map["hero"]) {
          const stats = safeParse(map["hero"].content, FALLBACK_HERO.stats);
          setHero({ title: map["hero"].title || FALLBACK_HERO.title, subtitle: map["hero"].subtitle || FALLBACK_HERO.subtitle, stats });
        }
        if (map["tour-options"]) {
          const items = safeParse(map["tour-options"].content, null);
          if (items?.length) setTourOptions(items);
          setToursMeta({ title: map["tour-options"].title || toursMeta.title, subtitle: map["tour-options"].subtitle || toursMeta.subtitle });
        }
        if (map["highlights"]) {
          const items = safeParse(map["highlights"].content, null);
          if (items?.length) setCampusHighlights(items);
          setHlMeta({ title: map["highlights"].title || hlMeta.title, subtitle: map["highlights"].subtitle || hlMeta.subtitle });
        }
        if (map["gallery"]) {
          const items = safeParse(map["gallery"].content, null);
          if (items?.length) setCampusPhotos(items);
          setGalMeta({ title: map["gallery"].title || galMeta.title, subtitle: map["gallery"].subtitle || galMeta.subtitle });
        }
        if (map["visit-info"]) {
          const data = safeParse(map["visit-info"].content, null);
          if (data) setVisitData(data);
          if (map["visit-info"].title) setVisitTitle(map["visit-info"].title);
        }
        if (map["contact"]) {
          const data = safeParse(map["contact"].content, null);
          if (data) setContactData(data);
          if (map["contact"].title) setContactTitle(map["contact"].title);
        }
      } catch (err) {
        console.error("Failed to load campus tour data from CMS:", err);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showCmsSection = (key) =>
    visibleSectionKeys == null || visibleSectionKeys.includes(key);

  const nextImage = () => {
    if (campusPhotos.length === 0) return;
    setCurrentImageIndex((prev) => (prev + 1) % campusPhotos.length);
  };
  const prevImage = () => {
    if (campusPhotos.length === 0) return;
    setCurrentImageIndex((prev) => (prev - 1 + campusPhotos.length) % campusPhotos.length);
  };
  const openModal = (index) => {
    if (campusPhotos.length === 0) return;
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const getTourAction = (tourId) => {
    if (tourId === "virtual") return () => window.location.assign("/admissions");
    return () => setSelectedTour(tourId);
  };

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">

      {/* Hero Section with Background Video */}
      {showCmsSection("hero") && (
      <section className="relative text-white py-20 lg:py-32 overflow-hidden">
        <video className="absolute inset-0 w-full h-full object-cover" src={campusDrone} autoPlay loop muted playsInline />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">{hero.title}</h1>
          <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto">{hero.subtitle}</p>
          <div className="flex flex-wrap justify-center gap-8 text-center">
            {hero.stats.map((s, i) => {
              const Icon = ICON_MAP[s.icon] || FaMapMarkerAlt;
              return (
                <div key={i} className="flex items-center gap-2">
                  <Icon className="text-gold text-2xl" />
                  <span className="text-lg font-semibold">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Tour Options */}
      {showCmsSection("tour-options") && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{toursMeta.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{toursMeta.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {tourOptions.map((tour) => {
              const IconComponent = ICON_MAP[tour.icon] || FaPlay;
              return (
                <div
                  key={tour.id}
                  className={`bg-white rounded-xl shadow-lg p-8 transition-all duration-300 cursor-pointer ${selectedTour === tour.id ? "ring-2 ring-custom-pink shadow-xl transform scale-105" : "hover:shadow-xl hover:scale-105"}`}
                  onClick={() => setSelectedTour(tour.id)}
                >
                  <div className="text-center mb-6">
                    <div className="bg-custom-pink text-white p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <IconComponent className="text-2xl" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tour.title}</h3>
                    <p className="text-gray-600 mb-4">{tour.description}</p>
                    <div className="flex items-center justify-center gap-2 text-sm text-gold font-semibold">
                      <FaClock className="text-sm" />
                      <span>{tour.duration}</span>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {tour.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-gold rounded-full"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={(e) => { e.stopPropagation(); getTourAction(tour.id)(); }}
                    className="w-full bg-gold text-white py-3 px-6 rounded-lg hover:bg-custom-pink transition-colors duration-300 font-semibold flex items-center justify-center gap-2"
                  >
                    {tour.buttonText}
                    <FaArrowRight className="text-sm" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Campus Highlights — only when CMS provides items with optional image URLs */}
      {showCmsSection("highlights") && campusHighlights.length > 0 && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{hlMeta.title}</h2>
            <p className="text-lg text-gray-600">{hlMeta.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {campusHighlights.map((highlight, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                {highlight.image ? (
                <div className="relative h-48 overflow-hidden">
                  <img src={highlight.image} alt={highlight.name || ""} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
                ) : (
                <div className="h-48 bg-gray-100 border-b border-gray-200" aria-hidden />
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{highlight.name}</h3>
                  <p className="text-gray-600 mb-4">{highlight.description}</p>
                  <div className="space-y-2">
                    {(highlight.features || []).map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Campus Photo Gallery */}
      {showCmsSection("gallery") && campusPhotos.length > 0 && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{galMeta.title}</h2>
            <p className="text-lg text-gray-600">{galMeta.subtitle}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 xl:grid-cols-4 gap-4">
            {campusPhotos.map((photo, index) => (
              <div key={index} className="relative h-48 overflow-hidden rounded-lg cursor-pointer group" onClick={() => openModal(index)}>
                <img src={photo.src} alt={photo.alt || ""} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <FaCamera className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Visit Information & Contact */}
      {(showCmsSection("visit-info") || showCmsSection("contact")) && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`grid gap-12 ${
              showCmsSection("visit-info") && showCmsSection("contact")
                ? "lg:grid-cols-2"
                : "lg:grid-cols-1 max-w-3xl mx-auto"
            }`}
          >
            {showCmsSection("visit-info") && (
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-6">{visitTitle}</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gold text-white p-3 rounded-full"><FaCalendarAlt className="text-xl" /></div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Tour Schedule</h3>
                    <div className="space-y-1 text-gray-600">
                      <p><strong>Monday - Friday:</strong> {visitData.schedule.weekday}</p>
                      <p><strong>Saturday:</strong> {visitData.schedule.saturday}</p>
                      <p><strong>Sunday:</strong> {visitData.schedule.sunday}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-gold text-white p-3 rounded-full"><FaClock className="text-xl" /></div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">What to Expect</h3>
                    <ul className="space-y-1 text-gray-600">
                      {visitData.expectations.map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-gold text-white p-3 rounded-full"><FaUsers className="text-xl" /></div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Group Tours</h3>
                    <p className="text-gray-600">{visitData.groupTours}</p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {showCmsSection("contact") && (
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-6">{contactTitle}</h2>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="bg-custom-pink text-white p-3 rounded-full"><FaPhone className="text-xl" /></div>
                  <div><h3 className="text-lg font-bold text-gray-900">Phone</h3><p className="text-gray-600">{contactData.phone}</p></div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-custom-pink text-white p-3 rounded-full"><FaEnvelope className="text-xl" /></div>
                  <div><h3 className="text-lg font-bold text-gray-900">Email</h3><p className="text-gray-600">{contactData.email}</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-custom-pink text-white p-3 rounded-full"><FaMapMarkerAlt className="text-xl" /></div>
                  <div><h3 className="text-lg font-bold text-gray-900">Address</h3><p className="text-gray-600 whitespace-pre-line">{contactData.address}</p></div>
                </div>
              </div>
              <div className="mt-8">
                <Link to="/contact" className="block w-full bg-gold text-white py-3 px-6 rounded-lg hover:bg-custom-pink transition-colors duration-300 font-semibold text-center">
                  {contactData.scheduleButtonText}
                </Link>
              </div>
            </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Image Modal */}
      {isModalOpen && campusPhotos.length > 0 && campusPhotos[currentImageIndex] && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <button type="button" onClick={closeModal} className="absolute top-4 right-4 text-white text-2xl hover:text-gold transition-colors z-10">&times;</button>
            <img src={campusPhotos[currentImageIndex].src} alt={campusPhotos[currentImageIndex].alt || ""} className="max-w-full max-h-full object-contain rounded-lg" />
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
              <button onClick={prevImage} className="bg-white/20 text-white p-2 rounded-full hover:bg-white/40 transition-colors"><FaChevronLeft /></button>
              <button onClick={nextImage} className="bg-white/20 text-white p-2 rounded-full hover:bg-white/40 transition-colors"><FaChevronRight /></button>
            </div>
          </div>
        </div>
      )}

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default CampusTour;
